import { ConnectError } from '@bufbuild/connect';
import { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';

import { AssetType } from '@/shared/api/protocol-ts/model/dto_asset_pb';
import { FeatureFlag } from '@/shared/api/protocol-ts/model/dto_organization_pb';
import {
  ACCEPTED_FILE_TYPES_FOR_DROPZONE_BY_ASSET_TYPE,
  UPLOADING_IMAGE_MAX_FILES,
} from '@/shared/config';
import { useAppDispatch, useAppSelector } from '@/shared/hooks';
import { trackEvent } from '@/shared/lib';
import type { IconNames } from '@/shared/ui';
import { errorToastCaller, Icon, Modal, toastCaller } from '@/shared/ui';

import { ModalID, modalModel } from '@/entities/modal';
import { organizationModel } from '@/entities/organization';
import { patientModel } from '@/entities/patient';

import { useUploadAssetContext } from '@/features/uploadAsset';

import { getUploadDentalPhotosErrorMessage } from '../../lib/getUploadDentalPhotosErrorMessage.ts';
import { validateDentalMediaFileSize } from '../../lib/validateDentalMediaFileSize.ts';

import styles from './MobileDentalPhotoUploadMenu.module.scss';

type FileInputConfig = {
  accept: string;
  capture?: 'environment' | 'user';
  multiple?: boolean;
};

type ActionItem = {
  icon: IconNames;
  label: string;
  onClick: () => void;
};

type UploadContext = {
  patientID: string;
  patientFullName: string;
  albumID?: string;
};

const acceptedTypes =
  ACCEPTED_FILE_TYPES_FOR_DROPZONE_BY_ASSET_TYPE[
    AssetType.AssetType_Study_DentalPhoto
  ];

const acceptedInputValue = Object.entries(acceptedTypes)
  .flatMap(([mimeType, extensions]) => [mimeType, ...extensions])
  .join(',');

const isAcceptedFile = (file: File) =>
  Object.entries(acceptedTypes).some(([mimeType, extensions]) => {
    const lowerCaseFileName = file.name.toLowerCase();

    return (
      file.type === mimeType ||
      extensions.some((extension) =>
        lowerCaseFileName.endsWith(extension.toLowerCase()),
      )
    );
  });

export const MobileDentalPhotoUploadMenu = () => {
  const { formatMessage } = useIntl();
  const dispatch = useAppDispatch();
  const { startUploadAsset: startUploadPhoto } = useUploadAssetContext();

  const { visible, data } = useAppSelector(
    (state) => state.modal[ModalID.MobileDentalPhotoUploadMenu],
  );
  const { patientID, albumID } = data;

  const patientFullName = useAppSelector(
    patientModel.selectors.selectPatientFullName(patientID),
  );
  const isForbidUploadsFromWeb = useAppSelector(
    organizationModel.selectors.selectFeatureFlag(
      FeatureFlag.FeatureFlag_Forbid_UploadsFromWeb,
    ),
  );

  const handleClose = () => {
    dispatch(
      modalModel.actions.closeModal(ModalID.MobileDentalPhotoUploadMenu),
    );
  };

  const showValidationError = (file: File, errorCode?: string) => {
    toastCaller({
      type: 'error',
      heading: formatMessage({
        id: 'error.heading',
        defaultMessage: 'Error',
      }),
      message: getUploadDentalPhotosErrorMessage(
        [
          {
            file,
            errors: [
              {
                code: errorCode ?? 'file-invalid-type',
                message: '',
              },
            ],
          },
        ],
        formatMessage,
      ),
      autoClose: 10000,
    });
  };

  const getValidationErrorCode = (files: File[]) => {
    if (files.length > UPLOADING_IMAGE_MAX_FILES) {
      return {
        file: files[0],
        errorCode: undefined,
      };
    }

    const invalidTypeFile = files.find((file) => !isAcceptedFile(file));
    if (invalidTypeFile) {
      return {
        file: invalidTypeFile,
        errorCode: undefined,
      };
    }

    const invalidSizeFile = files.find((file) =>
      validateDentalMediaFileSize(file),
    );
    if (invalidSizeFile) {
      return {
        file: invalidSizeFile,
        errorCode: validateDentalMediaFileSize(invalidSizeFile)?.code,
      };
    }

    return null;
  };

  const uploadFiles = async (files: File[], context: UploadContext) => {
    if (files.length === 0 || isForbidUploadsFromWeb) {
      return;
    }

    const validationError = getValidationErrorCode(files);
    if (validationError) {
      showValidationError(validationError.file, validationError.errorCode);
      return;
    }

    dispatch(modalModel.actions.closeModal(ModalID.MobileOrderReportModal));

    trackEvent({
      eventName: context.albumID
        ? 'DentalPhotosAddPhotoToAlbumClicked'
        : 'DentalPhotosUploadButtonClicked',
      properties: {
        album_id: context.albumID,
      },
    });

    try {
      await startUploadPhoto({
        patientID: context.patientID,
        files,
        assetType: AssetType.AssetType_Study_DentalPhoto,
        albumID: context.albumID,
        meta: {
          studyType: AssetType.AssetType_Study_DentalPhoto,
          patientName: context.patientFullName,
          albumID: context.albumID,
        },
      });
    } catch (error) {
      errorToastCaller(ConnectError.from(error));
    }
  };

  const activeInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    return () => {
      activeInputRef.current?.remove();
    };
  }, []);

  const openFileInput = (config: FileInputConfig, context: UploadContext) => {
    activeInputRef.current?.remove();

    const input = document.createElement('input');
    input.type = 'file';
    input.accept = config.accept;
    if (config.capture) {
      input.setAttribute('capture', config.capture);
    }
    if (config.multiple) {
      input.multiple = true;
    }
    input.style.position = 'fixed';
    input.style.opacity = '0';
    input.style.pointerEvents = 'none';

    input.addEventListener('change', () => {
      const files = Array.from(input.files ?? []);
      input.remove();
      activeInputRef.current = null;
      uploadFiles(files, context);
    });

    activeInputRef.current = input;
    document.body.appendChild(input);
    input.click();
  };

  const handleTakePhoto = () => {
    const context = { patientID, patientFullName, albumID };
    handleClose();
    openFileInput({ accept: 'image/*', capture: 'environment' }, context);
  };

  const handleRecordVideo = () => {
    const context = { patientID, patientFullName, albumID };
    handleClose();
    openFileInput({ accept: 'video/*', capture: 'environment' }, context);
  };

  const handleUploadFromGallery = () => {
    const context = { patientID, patientFullName, albumID };
    handleClose();
    openFileInput({ accept: acceptedInputValue, multiple: true }, context);
  };

  const actionItems: ActionItem[] = [
    {
      icon: 'camera',
      label: formatMessage({
        id: 'dentalPhotos.upload.actionSheet.takePhoto',
        defaultMessage: 'Take photo',
      }),
      onClick: handleTakePhoto,
    },
    {
      icon: 'play',
      label: formatMessage({
        id: 'dentalPhotos.upload.actionSheet.recordVideo',
        defaultMessage: 'Record video',
      }),
      onClick: handleRecordVideo,
    },
    {
      icon: 'photo',
      label: formatMessage({
        id: 'dentalPhotos.upload.actionSheet.fromGallery',
        defaultMessage: 'Upload from gallery',
      }),
      onClick: handleUploadFromGallery,
    },
  ];

  return (
    <Modal
      data-component="mobile-dental-photo-upload-menu"
      title={formatMessage({
        id: 'dentalPhotos.upload.title',
        defaultMessage: 'Upload photos or videos',
      })}
      isOpen={visible}
      onCancel={handleClose}
      className={styles.container}
      headerClassName={styles.header}
      bodyClassName={styles.body}
      hideFooter
    >
      <div className={styles.menuBody}>
        {actionItems.map((item) => (
          <button
            key={item.label}
            type="button"
            className={styles.menuItem}
            onClick={item.onClick}
          >
            <Icon name={item.icon} size={24} className={styles.menuIcon} />
            <span>{item.label}</span>
          </button>
        ))}
      </div>
    </Modal>
  );
};
